import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PartnerConsentPreload } from '../../api/types';
import { Card } from '../../components/Card';
import { formatBirthDate } from '../../utils/formatDate';

interface PreloadStateProps {
  data: PartnerConsentPreload;
  submitting: boolean;
  onSubmit: () => void;
}

export function PreloadState({ data, submitting, onSubmit }: PreloadStateProps) {
  const { t, i18n } = useTranslation();
  const [consented, setConsented] = useState(false);
  const checkboxId = useId();

  const birthDate = formatBirthDate(
    data.birthdateDay,
    data.birthdateMonth,
    data.birthdateYear,
    i18n.resolvedLanguage ?? 'de-CH',
  );

  return (
    <Card>
      <h1>{t('preload.title')}</h1>
      <p className="lead">{t('preload.intro')}</p>

      <div className="field-grid" aria-label={t('preload.yourDataHeading')}>
        <div className="field">
          <span className="label">{t('preload.firstNameLabel')}</span>
          <span className="value">{data.firstName ?? '—'}</span>
        </div>
        <div className="field">
          <span className="label">{t('preload.lastNameLabel')}</span>
          <span className="value">{data.lastName ?? '—'}</span>
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="label">{t('preload.birthDateLabel')}</span>
          <span className="value">{birthDate ?? '—'}</span>
        </div>
      </div>

      <label className="consent-row" htmlFor={checkboxId}>
        <input
          id={checkboxId}
          type="checkbox"
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
          disabled={submitting}
        />
        <span className="consent-text">{t('preload.consentLabel')}</span>
      </label>

      <button
        type="button"
        className="btn-primary"
        onClick={onSubmit}
        disabled={!consented || submitting}
      >
        {submitting ? t('preload.submitting') : t('preload.submit')}
      </button>
    </Card>
  );
}
